import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { Company, CompanyResourceType } from '~/types/company';
import {
  BookkeepingTransactionTemplateBody,
  RegistrationTemplate,
  RegistrationTemplateCreate,
  RegistrationTemplateResourceType,
  RegistrationTemplateType,
  VATRegistrationTemplateBody,
} from '~/types/registration-template';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';

export const fetchRegistrationTemplates = async (companyId: Company['id']): Promise<RegistrationTemplate[]> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/registration-templates', {
    params: searchParams,
  });

  return deserialize<RegistrationTemplate>(data) as RegistrationTemplate[];
};

interface BaseCreateRegistrationTemplatePayload {
  companyId: string;
  name: string;
}

interface BookkeepingTransactionCreateRegistrationTemplatePayload extends BaseCreateRegistrationTemplatePayload {
  templateType: RegistrationTemplateType.BOOKKEEPING_TRANSACTION;
  body: BookkeepingTransactionTemplateBody;
}

interface VATIssuedCreateRegistrationTemplatePayload extends BaseCreateRegistrationTemplatePayload {
  templateType: RegistrationTemplateType.VAT_REGISTRATION_ISSUED;
  body: VATRegistrationTemplateBody;
}

interface VATReceivedCreateRegistrationTemplatePayload extends BaseCreateRegistrationTemplatePayload {
  templateType: RegistrationTemplateType.VAT_REGISTRATION_RECEIVED;
  body: VATRegistrationTemplateBody;
}

type CreateRegistrationTemplatePayload =
  | BookkeepingTransactionCreateRegistrationTemplatePayload
  | VATIssuedCreateRegistrationTemplatePayload
  | VATReceivedCreateRegistrationTemplatePayload;

class CreateRegistrationTemplateTransformer extends Transformer<CreateRegistrationTemplatePayload, {}> {
  constructor() {
    super();
    this.type = RegistrationTemplateResourceType;
    this.relationships = {
      company: ({ companyId }) =>
        transform()
          .withInput({ id: companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext(),
    };
  }

  transform(payload: CreateRegistrationTemplatePayload): JsonObject {
    return {
      name: payload.name,
      templateType: payload.templateType,
      body: payload.body,
    };
  }
}

export const deleteRegistrationTemplate = async (id: RegistrationTemplate['id']): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/registration-templates/${id}`);
};

export const createRegistrationTemplate = async (
  companyId: Company['id'],
  template: RegistrationTemplateCreate,
): Promise<RegistrationTemplate> => {
  const payload = transform<CreateRegistrationTemplatePayload, {}>()
    .withInput({ ...template, companyId })
    .withTransformer(new CreateRegistrationTemplateTransformer())
    .serialize();

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/registration-templates', payload);

  return deserialize<RegistrationTemplate>(data) as RegistrationTemplate;
};
